import { useEffect, useRef, useState } from 'react';
import {
  DefaultButton,
  Dropdown,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
} from '@fluentui/react';
import { getApiErrorMessage } from '@moko/utils';
import {
  getTenantSurcharges,
  saveTenantSurcharge,
  type IAutomaticSurchargeRule,
} from '../../services/automaticSurchargeService';

export default function TenantSurcharges({ tenantId }: { tenantId: number }) {
  const [rules, setRules] = useState<IAutomaticSurchargeRule[]>([]);
  const [selected, setSelected] = useState(0);
  const [override, setOverride] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [reload, setReload] = useState(0);
  const pending = useRef<AbortController | undefined>(undefined);
  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setRules([]);
    setSelected(0);
    setError(undefined);
    setSaved(false);
    getTenantSurcharges(controller)
      .then((current) => {
        if (controller.signal.aborted) return;
        setRules(current);
        setSelected(current[0]?.pricingItemId ?? 0);
        setOverride(current[0]?.tenantEnabled ?? null);
        setLoaded(true);
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(getApiErrorMessage(e, 'Unable to load tenant surcharges.'));
      });
    return () => {
      controller.abort();
      pending.current?.abort();
    };
  }, [tenantId, reload]);
  const rule = rules.find((entry) => entry.pricingItemId === selected);
  const save = async () => {
    if (!rule) return;
    const controller = new AbortController();
    pending.current = controller;
    setSaving(true);
    setSaved(false);
    setError(undefined);
    try {
      await saveTenantSurcharge(controller, rule.pricingItemId, override);
      const current = await getTenantSurcharges(controller);
      if (controller.signal.aborted) return;
      setRules(current);
      setOverride(
        current.find((entry) => entry.pricingItemId === selected)
          ?.tenantEnabled ?? null
      );
      setSaved(true);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(getApiErrorMessage(e, 'Unable to save tenant surcharge.'));
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };
  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { maxWidth: 720 } }}>
      <Text>
        Override surcharge defaults for the selected tenant. Use global setting
        removes the override and follows future global changes. Rates remain in
        Rate overrides.
      </Text>
      {!loaded && !error && <Spinner label="Loading tenant surcharges…" />}
      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}
      {!loaded && error && (
        <DefaultButton text="Retry" onClick={() => setReload((n) => n + 1)} />
      )}
      {saved && (
        <MessageBar messageBarType={MessageBarType.success}>
          Tenant surcharge saved.
        </MessageBar>
      )}
      {loaded && rules.length === 0 && (
        <Text>
          No automatic surcharges are configured. Add them in Global settings →
          Automatic surcharges.
        </Text>
      )}
      <Dropdown
        label="Surcharge"
        options={rules.map((entry) => ({
          key: entry.pricingItemId,
          text: `${entry.name} (${entry.code})`,
        }))}
        selectedKey={selected || undefined}
        disabled={!loaded || saving}
        onChange={(_, option) => {
          if (option) {
            setSelected(Number(option.key));
            setOverride(
              rules.find((entry) => entry.pricingItemId === option.key)
                ?.tenantEnabled ?? null
            );
            setSaved(false);
            setError(undefined);
          }
        }}
      />
      {rule && (
        <>
          <Text>Global default: {rule.enabled ? 'Enabled' : 'Disabled'}</Text>
          <Text>
            Effective setting: {rule.effectiveEnabled ? 'Enabled' : 'Disabled'}
          </Text>
          <Text>
            Site condition:{' '}
            {rule.countryCode
              ? [rule.countryCode, rule.stateCode].filter(Boolean).join(' / ')
              : 'All locations'}
          </Text>
          <Dropdown
            label="Tenant setting"
            selectedKey={
              override === null ? 'inherit' : override ? 'enabled' : 'disabled'
            }
            disabled={saving}
            options={[
              { key: 'inherit', text: 'Use global setting' },
              { key: 'enabled', text: 'Enabled' },
              { key: 'disabled', text: 'Disabled' },
            ]}
            onChange={(_, option) => {
              if (option) {
                setOverride(
                  option.key === 'inherit' ? null : option.key === 'enabled'
                );
                setSaved(false);
                setError(undefined);
              }
            }}
          />
          <Stack horizontal>
            <PrimaryButton
              text={saving ? 'Saving…' : 'Save tenant setting'}
              disabled={saving || override === (rule.tenantEnabled ?? null)}
              onClick={save}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
}
